import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Mail, Search, RefreshCw, User, Calendar, MessageCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import pb from '../../lib/pocketbaseClient';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; }
};

const ContactInquiries = () => {
  const [search, setSearch] = useState('');

  // Fetch leads where source = "contact" (or you can use a different filter)
  // If you don't have a source field, you can filter by status = "Lead"
  const { data: inquiries = [], isLoading, refetch } = useQuery({
    queryKey: ['contactInquiries'],
    queryFn: async () => {
      // Option 1: filter by source = "contact" (if you added this field)
      // return pb.collection('leads').getFullList({ filter: 'source = "contact"', sort: '-created' });

      // Option 2: filter by status = "Lead" and maybe name starts with "Contact"
      return pb.collection('leads').getFullList({
        filter: 'status = "Lead"',
        sort: '-created',
      });
    },
  });

  const filtered = inquiries.filter(inq =>
    inq.name?.toLowerCase().includes(search.toLowerCase()) ||
    inq.email?.toLowerCase().includes(search.toLowerCase()) ||
    inq.phone?.includes(search) ||
    inq.requirement?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-6 shadow-xl">
        <div className="relative">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Mail className="w-6 h-6" /> Contact Inquiries
          </h1>
          <p className="text-white/80">All messages sent via the contact form</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 items-center bg-white p-4 rounded-xl shadow-sm border border-slate-200">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by name, email, phone, requirement..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Button variant="outline" onClick={() => refetch()}><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
      </div>

      <Card className="border-0 shadow-xl overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
              <TableRow>
                <TableHead className="text-indigo-700 font-semibold">Date</TableHead>
                <TableHead className="text-indigo-700 font-semibold">Name</TableHead>
                <TableHead className="text-indigo-700 font-semibold">Email</TableHead>
                <TableHead className="text-indigo-700 font-semibold">Phone</TableHead>
                <TableHead className="text-indigo-700 font-semibold">Requirement</TableHead>
                <TableHead className="text-indigo-700 font-semibold">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8">Loading...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No inquiries found</TableCell></TableRow>
              ) : (
                filtered.map((inq) => (
                  <TableRow key={inq.id} className="hover:bg-indigo-50/50 transition-colors">
                    <TableCell className="text-sm">{formatDate(inq.created)}</TableCell>
                    <TableCell className="font-medium">{inq.name}</TableCell>
                    <TableCell>{inq.email || '—'}</TableCell>
                    <TableCell>{inq.phone || '—'}</TableCell>
                    <TableCell className="max-w-xs truncate">{inq.requirement || '—'}</TableCell>
                    <TableCell>
                      <Badge className="bg-amber-100 text-amber-700">Lead</Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactInquiries;